import api from '../../../lib/api';
import type {
  EnvironmentalAspectPayload,
  EnvironmentalAspectSummary,
  EnvironmentalCriteriaConfig,
  PaginatedEnvironmentalAspects,
  UpdateCriteriaConfigPayload,
  UpdateCriteriaConfigResult,
} from './types';

export interface ListEnvironmentalAspectsParams {
  page?: number;
  pageSize?: number;
  condition?: string;
  significant?: boolean;
  status?: string;
  lifecycleStage?: string;
  legalRequirementId?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface LifecycleReportRow {
  lifecycleStage: string;
  total: number;
  significantCount: number;
  withoutControlCount: number;
}

export const fetchLifecycleReport = async (): Promise<LifecycleReportRow[]> => {
  const { data } = await api.get<{ data: LifecycleReportRow[] }>(
    'environmental-aspects/lifecycle-report',
  );
  return data.data;
};

export const fetchEnvironmentalAspects = async (
  params: ListEnvironmentalAspectsParams = {},
): Promise<PaginatedEnvironmentalAspects> => {
  const { data } = await api.get<PaginatedEnvironmentalAspects>('environmental-aspects', {
    params,
  });
  return data;
};

export const fetchEnvironmentalAspect = async (
  id: string,
): Promise<EnvironmentalAspectSummary> => {
  const { data } = await api.get<EnvironmentalAspectSummary>(`environmental-aspects/${id}`);
  return data;
};

export const createEnvironmentalAspect = async (payload: EnvironmentalAspectPayload) => {
  const { data } = await api.post('environmental-aspects', payload);
  return data;
};

export const updateEnvironmentalAspect = async (
  id: string,
  payload: Partial<EnvironmentalAspectPayload>,
) => {
  const { data } = await api.put(`environmental-aspects/${id}`, payload);
  return data;
};

export const deleteEnvironmentalAspect = async (id: string) => {
  const { data } = await api.delete(`environmental-aspects/${id}`);
  return data;
};

export const escalateEnvironmentalAspect = async (
  id: string,
  payload?: { title?: string; description?: string },
) => {
  const { data } = await api.post(`environmental-aspects/${id}/escalate`, payload ?? {});
  return data as { aspectId: string; qmsRecordId: string };
};

export const fetchEnvironmentalCriteriaConfig = async (): Promise<EnvironmentalCriteriaConfig> => {
  const { data } = await api.get<EnvironmentalCriteriaConfig>(
    'environmental-aspects/criteria-config',
  );
  return data;
};

export const updateEnvironmentalCriteriaConfig = async (
  payload: UpdateCriteriaConfigPayload,
): Promise<UpdateCriteriaConfigResult> => {
  const { data } = await api.put<UpdateCriteriaConfigResult>(
    'environmental-aspects/criteria-config',
    payload,
  );
  return data;
};
